export type ApiErrorKind =
  | "network"
  | "timeout"
  | "http"
  | "service"
  | "protocol"
  | "empty-resource";

export interface ApiErrorOptions {
  kind: ApiErrorKind;
  outerStatus?: number;
  serviceStatus?: number;
  serviceName?: string;
  payload?: unknown;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | undefined;
  readonly outerStatus: number | undefined;
  readonly httpStatus: number | undefined;
  readonly serviceStatus: number | undefined;
  readonly serviceName: string | undefined;
  readonly payload: unknown;
  readonly code: string | undefined;

  constructor(message: string, options: ApiErrorOptions) {
    super(message);
    this.name = "ApiError";
    this.kind = options.kind;
    this.outerStatus = options.outerStatus;
    this.httpStatus = options.outerStatus;
    this.serviceStatus = options.serviceStatus;
    this.status = options.serviceStatus ?? options.outerStatus;
    this.serviceName = options.serviceName;
    this.payload = options.payload;
    this.code = getPayloadCode(options.payload);

    if (options.cause !== undefined) {
      this.cause = options.cause;
    }
  }
}

export type PublicEntityErrorStatus = 404 | 410 | 503;

export function mapApiErrorToPublicStatus(
  error: unknown,
  expectedResourceId?: string,
): PublicEntityErrorStatus {
  if (!(error instanceof ApiError)) return 503;
  if (
    error.kind === "network" ||
    error.kind === "timeout" ||
    error.kind === "protocol"
  )
    return 503;

  const status = error.serviceStatus ?? error.outerStatus;
  if (status === 429 || (status !== undefined && status >= 500)) return 503;
  if (
    status === 410 &&
    isMatchingResourceTombstone(error.payload, expectedResourceId)
  ) {
    return 410;
  }
  if (
    error.kind === "empty-resource" ||
    status === 401 ||
    status === 403 ||
    status === 404 ||
    status === 410
  ) {
    return 404;
  }

  return 503;
}

export function isMatchingResourceTombstone(
  payload: unknown,
  expectedResourceId?: string,
): boolean {
  const errorPayload = nestedErrorPayload(payload);
  if (getPayloadCode(payload) !== "RESOURCE_TOMBSTONE") return false;
  if (!expectedResourceId) return true;

  const payloadResourceId =
    getPayloadResourceId(errorPayload) ?? getPayloadResourceId(payload);
  return (
    payloadResourceId !== undefined &&
    payloadResourceId.toLowerCase() === expectedResourceId.toLowerCase()
  );
}

function getPayloadCode(payload: unknown): string | undefined {
  if (!isRecord(payload)) return undefined;
  if (typeof payload.code === "string") return payload.code;

  const errorPayload = nestedErrorPayload(payload);
  if (errorPayload && typeof errorPayload.code === "string") {
    return errorPayload.code;
  }

  return undefined;
}

function getPayloadResourceId(payload: unknown): string | undefined {
  if (!isRecord(payload)) return undefined;
  for (const key of ["resourceId", "resource_id", "id"]) {
    if (typeof payload[key] === "string") return payload[key];
  }
  return undefined;
}

function nestedErrorPayload(
  payload: unknown,
): Record<string, unknown> | undefined {
  if (!isRecord(payload)) return undefined;
  return isRecord(payload.error) ? payload.error : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
